import { isPrV2Route } from '@/lib/pr-v2/route-utils';

function isCodePath(pathname: string) {
  return pathname === '/code' || pathname.startsWith('/code/');
}

/**
 * /code pages with the code session list and composer. Fix sessions (`/code/fix`) list their
 * runs without session shortcuts and take no prompts, so nothing there stops a run by hotkey.
 */
function isCodeSessionPath(pathname: string) {
  const onFixSessions = pathname === '/code/fix' || pathname.startsWith('/code/fix/');
  return isCodePath(pathname) && !onFixSessions;
}

export type KeyboardShortcutScope = 'global' | 'inbox' | 'pr-v2' | 'review' | 'diff';

export type KeyboardShortcutGroup =
  | 'PR Review'
  | 'Navigation'
  | 'Merge'
  | 'Diff View'
  | 'Chat & Sidebar'
  | 'Copy & Open'
  | 'Pull requests'
  | 'Global';

export type KeyboardShortcutContext = 'global' | 'inbox' | 'pr-v2';

/** What a `showWhen` decides on: the page, /code access and whether it is the desktop app. */
export type KeyboardShortcutFilterContext = {
  pathname: string;
  hasCodeAccess: boolean;
  isDesktopApp: boolean;
};

export type KeyboardShortcutEntry = {
  id: string;
  label: string;
  keys: readonly (readonly string[])[];
  /** Further sequences only the desktop app takes, listed after `keys` there. */
  desktopKeys?: readonly (readonly string[])[];
  group: KeyboardShortcutGroup;
  scope: KeyboardShortcutScope;
  keywords?: readonly string[];
  showWhen?: (context: KeyboardShortcutFilterContext) => boolean;
};

const PR_SHORTCUT_GROUPS: KeyboardShortcutGroup[] = [
  'PR Review',
  'Navigation',
  'Merge',
  'Diff View',
  'Chat & Sidebar',
  'Copy & Open',
];

const APP_SHORTCUT_GROUPS: KeyboardShortcutGroup[] = [
  'Global',
  'Pull requests',
  ...PR_SHORTCUT_GROUPS,
];

const PR_ROUTE_GROUP_ORDER: KeyboardShortcutGroup[] = [
  ...PR_SHORTCUT_GROUPS,
  'Global',
  'Pull requests',
];

const INBOX_ROUTE_GROUP_ORDER: KeyboardShortcutGroup[] = [
  'Pull requests',
  ...APP_SHORTCUT_GROUPS.filter((group) => group !== 'Pull requests'),
];

const GLOBAL_GROUP_ORDER: KeyboardShortcutGroup[] = APP_SHORTCUT_GROUPS;

export const KEYBOARD_SHORTCUT_GROUP_ORDER: Record<
  KeyboardShortcutContext,
  KeyboardShortcutGroup[]
> = {
  'global': GLOBAL_GROUP_ORDER,
  'inbox': INBOX_ROUTE_GROUP_ORDER,
  'pr-v2': PR_ROUTE_GROUP_ORDER,
};

export const KEYBOARD_SHORTCUTS = [
  {
    id: 'global-open-command-menu',
    label: 'Open command menu',
    keys: [['Mod', 'K']],
    group: 'Global',
    scope: 'global',
    keywords: ['command palette', 'search commands'],
  },
  {
    id: 'desktop-open-command-menu',
    label: 'Open command menu from any app',
    keys: [['ControlOrSuper', 'Alt', 'C']],
    group: 'Global',
    scope: 'global',
    keywords: ['desktop', 'hotkey', 'clipboard'],
    showWhen: ({ isDesktopApp }) => isDesktopApp,
  },
  {
    id: 'global-show-shortcuts',
    label: 'Show keyboard shortcuts',
    keys: [['?']],
    group: 'Global',
    scope: 'global',
    keywords: ['help', 'hotkeys'],
  },
  {
    id: 'global-go-inbox',
    label: 'Go to pull requests',
    keys: [['G', 'I']],
    group: 'Global',
    scope: 'global',
    keywords: ['navigation', 'home', 'inbox'],
  },
  {
    id: 'global-go-settings',
    label: 'Go to settings',
    keys: [
      ['G', 'S'],
      ['Mod', ','],
    ],
    group: 'Global',
    scope: 'global',
    keywords: ['navigation', 'preferences'],
  },
  {
    id: 'global-go-ai-review',
    label: 'Go to AI review',
    keys: [['G', 'R']],
    group: 'Global',
    scope: 'global',
    keywords: ['navigation', 'review settings'],
  },
  {
    id: 'global-go-custom-agents',
    label: 'Go to custom agents',
    keys: [['G', 'C']],
    group: 'Global',
    scope: 'global',
    keywords: ['navigation', 'custom rules', 'ai review'],
  },
  {
    id: 'global-copy-url',
    label: 'Copy current URL',
    keys: [
      ['C', 'L'],
      ['Mod', 'Shift', 'C'],
    ],
    group: 'Global',
    scope: 'global',
    keywords: ['copy link'],
  },
  {
    id: 'code-new-session',
    label: 'New session',
    keys: [['Mod', 'Shift', 'O']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'composer'],
    showWhen: ({ hasCodeAccess }) => hasCodeAccess,
  },
  {
    id: 'code-toggle-sidebar',
    label: 'Toggle sidebar',
    keys: [['Mod', 'B']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'session list', 'toggle'],
    showWhen: ({ pathname }) => isCodePath(pathname),
  },
  {
    id: 'code-jump-to-session',
    label: 'Go to session 1–9',
    keys: [['Alt', '1–9']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'session list', 'switch', 'option'],
    showWhen: ({ pathname }) => isCodeSessionPath(pathname),
  },
  {
    id: 'code-previous-session',
    label: 'Previous session',
    keys: [['Alt', '↑']],
    desktopKeys: [['Mod', 'Shift', '[']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'session list', 'switch', 'up'],
    showWhen: ({ pathname }) => isCodeSessionPath(pathname),
  },
  {
    id: 'code-next-session',
    label: 'Next session',
    keys: [['Alt', '↓']],
    desktopKeys: [['Mod', 'Shift', ']']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'session list', 'switch', 'down'],
    showWhen: ({ pathname }) => isCodeSessionPath(pathname),
  },
  {
    id: 'code-stop-turn',
    label: 'Stop the running turn',
    keys: [['Esc', 'Esc']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'interrupt', 'cancel', 'escape'],
    showWhen: ({ pathname }) => isCodeSessionPath(pathname),
  },
  {
    // From the start of the composer's text, where ⌥↑ steps through sessions and ↑ recalls
    // earlier prompts.
    id: 'code-edit-last-queued',
    label: 'Edit the last editable queued message',
    keys: [['Alt', 'Shift', '↑']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'queue', 'edit'],
    showWhen: ({ pathname }) => isCodeSessionPath(pathname),
  },
  {
    // From the composer, where ⌘↵ steers the text being written.
    id: 'code-steer-first-queued',
    label: 'Steer the first queued message',
    keys: [['Mod', 'Shift', 'Enter']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'queue', 'steer', 'send now'],
    showWhen: ({ pathname }) => isCodeSessionPath(pathname),
  },
  {
    id: 'code-find-in-conversation',
    label: 'Find in conversation',
    keys: [['Mod', 'F']],
    group: 'Global',
    scope: 'global',
    keywords: ['code', 'search', 'find'],
    showWhen: ({ pathname }) => pathname.startsWith('/code/'),
  },
  {
    id: 'inbox-next-pr',
    label: 'Next pull request',
    keys: [['J'], ['↓']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['navigate', 'move down'],
  },
  {
    id: 'inbox-previous-pr',
    label: 'Previous pull request',
    keys: [['K'], ['↑']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['navigate', 'move up'],
  },
  {
    id: 'inbox-open-selected-pr',
    label: 'Open selected pull request',
    keys: [['Enter'], ['→']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['open', 'select'],
  },
  {
    id: 'inbox-approve-pr',
    label: 'Approve selected pull request',
    keys: [['Shift', 'A']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['approve', 'review'],
    showWhen: ({ pathname }) => pathname === '/inbox',
  },
  {
    id: 'inbox-merge-pr',
    label: 'Merge selected pull request',
    keys: [['Shift', 'M']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['merge', 'ready to merge'],
    showWhen: ({ pathname }) => pathname === '/inbox',
  },
  {
    id: 'inbox-snooze-pr',
    label: 'Snooze selected pull request',
    keys: [['H']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['snooze', 'later', 'remind', 'hide'],
    showWhen: ({ pathname }) => pathname === '/inbox',
  },
  {
    id: 'inbox-open-pr-github',
    label: 'Open selected pull request in GitHub',
    keys: [['Shift', 'G']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['github', 'external'],
    showWhen: ({ pathname }) => pathname === '/inbox',
  },
  {
    id: 'inbox-select-repositories',
    label: 'Select repositories',
    keys: [['R']],
    group: 'Pull requests',
    scope: 'inbox',
    keywords: ['repository filter'],
  },
  {
    id: 'pr-review-open-review-menu',
    label: 'Open review menu',
    keys: [['R']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['review dropdown'],
  },
  {
    id: 'pr-reviewers-edit',
    label: 'Edit reviewers',
    keys: [['E', 'R']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['add reviewer', 'request review'],
  },
  {
    id: 'pr-review-approve',
    label: 'Approve review',
    keys: [['Shift', 'A']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['approve'],
  },
  {
    id: 'pr-review-request-changes',
    label: 'Request changes',
    keys: [['Shift', 'R']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['review', 'changes'],
  },
  {
    id: 'pr-review-comment',
    label: 'Comment on PR',
    keys: [['Shift', 'C']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['leave review comment'],
  },
  {
    id: 'pr-review-submit',
    label: 'Submit review',
    keys: [['Mod', 'Enter']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['save', 'send'],
  },
  {
    id: 'pr-review-publish',
    label: 'Publish pull request',
    keys: [['Shift', 'P']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['publish', 'draft', 'ready for review'],
  },
  {
    id: 'pr-action-convert-draft',
    label: 'Convert to draft',
    keys: [['Shift', 'D']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['draft'],
  },
  {
    id: 'pr-action-close-reopen',
    label: 'Close or reopen PR',
    keys: [['Shift', 'X']],
    group: 'PR Review',
    scope: 'review',
    keywords: ['state', 'close', 'reopen'],
  },
  {
    id: 'pr-navigation-next-file',
    label: 'Next file',
    keys: [['J'], ['↓']],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['diff', 'file'],
  },
  {
    id: 'pr-navigation-previous-file',
    label: 'Previous file',
    keys: [['K'], ['↑']],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['diff', 'file'],
  },
  {
    id: 'pr-navigation-scroll-down',
    label: 'Scroll diff down',
    keys: [
      ['Shift', 'J'],
      ['Shift', '↓'],
    ],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['half page'],
  },
  {
    id: 'pr-navigation-scroll-up',
    label: 'Scroll diff up',
    keys: [
      ['Shift', 'K'],
      ['Shift', '↑'],
    ],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['half page'],
  },
  {
    id: 'pr-navigation-toggle-file-tree',
    label: 'Toggle file tree',
    keys: [['F']],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['sidebar', 'files'],
  },
  {
    id: 'pr-navigation-search-files',
    label: 'Search files',
    keys: [['Mod', 'Shift', 'F']],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['file tree', 'filter'],
  },
  {
    id: 'pr-navigation-view-stack',
    label: 'View stack',
    keys: [['S']],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['stacked pull requests'],
  },
  {
    id: 'pr-navigation-previous-stack-pr',
    label: 'Previous PR in stack',
    keys: [['[']],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['stack', 'parent'],
  },
  {
    id: 'pr-navigation-next-stack-pr',
    label: 'Next PR in stack',
    keys: [[']']],
    group: 'Navigation',
    scope: 'pr-v2',
    keywords: ['stack', 'child'],
  },
  {
    id: 'pr-merge-open-menu',
    label: 'Open merge menu',
    keys: [['M']],
    group: 'Merge',
    scope: 'pr-v2',
    keywords: ['merge dropdown'],
  },
  {
    id: 'pr-merge-primary-action',
    label: 'Run primary merge action',
    keys: [['Shift', 'M']],
    group: 'Merge',
    scope: 'pr-v2',
    keywords: ['merge', 'auto merge'],
  },
  {
    id: 'pr-diff-toggle-viewed',
    label: 'Mark active file viewed',
    keys: [['Space']],
    group: 'Diff View',
    scope: 'diff',
    keywords: ['viewed', 'file'],
  },
  {
    id: 'pr-diff-collapse-file',
    label: 'Collapse active file',
    keys: [['←']],
    group: 'Diff View',
    scope: 'diff',
    keywords: ['fold', 'file'],
  },
  {
    id: 'pr-diff-expand-file',
    label: 'Expand active file',
    keys: [['→']],
    group: 'Diff View',
    scope: 'diff',
    keywords: ['unfold', 'file'],
  },
  {
    id: 'pr-diff-toggle-file-comment',
    label: 'Toggle file comment draft',
    keys: [['C']],
    group: 'Diff View',
    scope: 'diff',
    keywords: ['comment', 'draft'],
  },
  {
    id: 'pr-diff-open-imported-file',
    label: 'Open the imported file',
    keys: [['Mod', 'Click']],
    group: 'Diff View',
    scope: 'diff',
    keywords: ['import', 'path', 'go to file'],
  },
  {
    id: 'pr-sidebar-chat',
    label: 'Open chat sidebar',
    keys: [['Mod', 'I']],
    group: 'Chat & Sidebar',
    scope: 'pr-v2',
    keywords: ['ask selected text', 'chat'],
  },
  {
    id: 'pr-toggle-sidebar',
    label: 'Toggle chat sidebar',
    keys: [['Mod', 'B']],
    group: 'Chat & Sidebar',
    scope: 'pr-v2',
    keywords: ['collapse', 'expand', 'chat'],
  },
  {
    id: 'pr-view-zen-mode',
    label: 'Toggle zen mode',
    keys: [['Mod', '\\']],
    group: 'Chat & Sidebar',
    scope: 'pr-v2',
    keywords: ['focus', 'workspace'],
  },
  {
    id: 'pr-open-github',
    label: 'Open in GitHub',
    keys: [['Shift', 'G']],
    group: 'Copy & Open',
    scope: 'pr-v2',
    keywords: ['github', 'external'],
  },
  {
    id: 'pr-open-preview',
    label: 'Open preview deployment',
    keys: [['Alt', 'P']],
    group: 'Copy & Open',
    scope: 'pr-v2',
    keywords: ['vercel', 'deployment'],
  },
  {
    id: 'pr-copy-link',
    label: 'Copy PR link',
    keys: [['C', 'L']],
    group: 'Copy & Open',
    scope: 'pr-v2',
    keywords: ['url'],
  },
  {
    id: 'pr-copy-branch',
    label: 'Copy branch name',
    keys: [['C', 'B']],
    group: 'Copy & Open',
    scope: 'pr-v2',
    keywords: ['head branch'],
  },
  {
    id: 'pr-copy-hash',
    label: 'Copy branch hash',
    keys: [['C', 'H']],
    group: 'Copy & Open',
    scope: 'pr-v2',
    keywords: ['sha'],
  },
  {
    id: 'pr-copy-title-link',
    label: 'Copy title as link',
    keys: [['C', 'T']],
    group: 'Copy & Open',
    scope: 'pr-v2',
    keywords: ['markdown', 'copy'],
  },
] as const satisfies readonly KeyboardShortcutEntry[];

/** A `KEYBOARD_SHORTCUTS` id, so a command naming an unknown shortcut fails to compile. */
export type KeyboardShortcutId = (typeof KEYBOARD_SHORTCUTS)[number]['id'];

export function getKeyboardShortcutContext(pathname: string): KeyboardShortcutContext {
  if (isPrV2Route(pathname)) {
    return 'pr-v2';
  }

  if (pathname === '/inbox' || pathname.startsWith('/inbox/')) {
    return 'inbox';
  }

  return 'global';
}

export function shouldEnableKeyboardShortcutsDrawer(pathname: string | null | undefined) {
  return !!pathname && !pathname.startsWith('/onboarding');
}

export function getKeyboardShortcutsForPath(
  pathname: string,
  { isDesktopApp, hasCodeAccess }: { isDesktopApp: boolean; hasCodeAccess: boolean },
) {
  const context: KeyboardShortcutFilterContext = { pathname, hasCodeAccess, isDesktopApp };
  const entries: readonly KeyboardShortcutEntry[] = KEYBOARD_SHORTCUTS;
  return entries
    .filter((entry) => !entry.showWhen || entry.showWhen(context))
    .map((entry) =>
      isDesktopApp && entry.desktopKeys
        ? { ...entry, keys: [...entry.keys, ...entry.desktopKeys] }
        : entry,
    );
}
